import { Store } from 'redux';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import MockAdapter from 'axios-mock-adapter';
import axiosRetry from 'axios-retry';

import { IAllActions, IStore } from 'redux/root';
import { resetAccountStore } from 'redux/account/actions';
import { enqueueSnackbar } from 'redux/snackbar/actions';
import { hideLoader } from 'redux/loader/actions';

import { publicURL, authenticatedURL } from 'stubs';
import { IGQLResponse } from 'stubs/graphql/response';
import { mockPublicGQL, mockAuthenticatedGQL } from 'stubs/graphql/_mocks';

const defaultErrMsg = 'Sorry, something went wrong!';
const sessionErrMsg = 'The session has expired.';

const publicPaths = [
  '',
  '/login',
  '/password/forgot',
  '/password/reset',
  '/swap',
  '/item',
];

const isPublicPage = (pathname: string) => {
  // Remove trailing slash from pathname if present
  pathname = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  const index = publicPaths.findIndex((it) => {
    // Exact match
    if (it === '') {
      return it === pathname;
    }

    // Prefix match
    return pathname.startsWith(it);
  });

  return index >= 0;
};

const requestHandler = (request: AxiosRequestConfig) => {
  if (
    request.url &&
    request.url.startsWith(process.env.REACT_APP_API_URL as string)
  ) {
    // Use cookies
    request.withCredentials = true;
  }

  return request;
};

const responseHandler =
  (store: Store<IStore, IAllActions>) => (response: AxiosResponse<any>) => {
    // Not a GraphQL response, no handling required
    if (!response.config.url?.includes('graphql')) {
      return response;
    }

    const { errors } = response?.data as IGQLResponse;
    const hasGQLErrors = Array.isArray(errors) && errors.length > 0;
    const hasGQLData = response?.data?.data;

    if (hasGQLErrors) {
      // Retrieve the first error object
      const { extensions } = errors[0];
      // Retrieve its error message
      const errMsg =
        extensions.status >= 500 ? defaultErrMsg : extensions.error_description;

      // Display error message
      store.dispatch(
        enqueueSnackbar({
          message: errMsg,
          options: {
            variant: 'error',
          },
        })
      );

      return Promise.reject(extensions);
    }

    if (!hasGQLData) {
      // Unhandled error, display generic error message
      store.dispatch(
        enqueueSnackbar({
          message: defaultErrMsg,
          options: {
            variant: 'error',
          },
        })
      );

      return Promise.reject(defaultErrMsg);
    }

    return response;
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const errorHandler =
  (store: Store<IStore, IAllActions>) =>
  async (error: any): Promise<any> => {
    store.dispatch(hideLoader());

    const status = error?.response?.status;

    if (status >= 500) {
      // Unhandled error, display generic error message
      store.dispatch(
        enqueueSnackbar({
          message: defaultErrMsg,
          options: {
            variant: 'error',
            autoHideDuration: 3000,
          },
        })
      );
    }

    if (status === 401) {
      if (!isPublicPage(window.location.pathname)) {
        // Display session expired error message
        store.dispatch(
          enqueueSnackbar({
            message: sessionErrMsg,
            options: {
              variant: 'error',
              autoHideDuration: 3000,
            },
          })
        );
      }

      // Resetting account store triggers redirection to login page
      store.dispatch(resetAccountStore());
    } else if (error?.response?.data) {
      const { code, description } = error.response.data;
      const msg = description || code;
      store.dispatch(
        enqueueSnackbar({
          message: msg,
          options: {
            variant: 'error',
            autoHideDuration: 3000,
          },
        })
      );
    }

    return Promise.reject(error);
  };

export const configureInterceptors = (
  store: Store<IStore, IAllActions>
): void => {
  axios.interceptors.request.use(requestHandler);
  axios.interceptors.response.use(responseHandler(store), errorHandler(store));
};

export const configureStubs = (store: Store<IStore, IAllActions>): void => {
  if (process.env.REACT_APP_MOCK_SERVER_RESPONSES === 'true') {
    configureMockServerResponses();
  }

  axiosRetry(axios, { retries: 3, retryDelay: () => 500 });

  configureInterceptors(store);
};

const configureMockServerResponses = () => {
  const mockAxios = new MockAdapter(axios);

  // Configure public GraphQL mock mapping
  mockAxios.onPost(publicURL).reply((config) => {
    const { query, variables } = JSON.parse(config.data);

    return mockPublicGQL[query](variables);
  });

  // Configure authenticated GraphQL mock mapping
  mockAxios.onPost(authenticatedURL).reply((config) => {
    const { query, variables } = JSON.parse(config.data);

    return mockAuthenticatedGQL[query](variables);
  });
};
